import React, { useState } from 'react';
import { IconButton } from '../../../../../../components/IconButton';
import { OfferRequisition } from '../../../../../../entities/v1/applicant_tracking/OfferRequisition';
import { User } from '../../../../../../entities/User';
import { AlertObject, AlertType } from '../../../../../../components/Alert';
import {
  RequisitionStatus,
  RequisitionApproverAnswer,
} from '../../../../../../entities/v1/applicant_tracking/Requisition';
import { SendReminderModal } from './SendReminderModal';
import { Button } from 'reactstrap';
import { SpanTooltip } from '../../../../../../components/SpanTooltip';
import { Timedelta, MINUTES_IN_DAY } from '../../../../../../utils/timedelta';

interface PropTypes {
  currentUser: User;
  offerRequisition: OfferRequisition;
  onEditOfferRequisition: () => void;
  setAlertMessage: (message: AlertObject) => void;
  reloadOfferRequisition: () => void;
}

interface SendReminderButtonPropTypes extends PropTypes {
  disabled?: boolean;
  onSendReminderClick: () => void;
}

function isEditable(props: PropTypes): boolean {
  return (
    props.offerRequisition.status === RequisitionStatus.Declined ||
    !props.offerRequisition.approvers.some(
      (approver) => approver.answer === RequisitionApproverAnswer.Approved,
    )
  );
}

function SendReminderButton(props: SendReminderButtonPropTypes) {
  if (props.offerRequisition.status !== RequisitionStatus.Pending) return;

  return (
    <Button
      color='secondary'
      className='me-3'
      onClick={props.onSendReminderClick}
      disabled={props.disabled}
    >
      Send Reminder
    </Button>
  );
}

function SendReminder(props: SendReminderButtonPropTypes) {
  const reminderEnabled =
    !props.offerRequisition.reminderSentAt ||
    Timedelta.timeTillDate(props.offerRequisition.reminderSentAt).toMins() <
      -MINUTES_IN_DAY;

  return reminderEnabled ? (
    <SendReminderButton {...props} />
  ) : (
    <SpanTooltip
      text={<SendReminderButton disabled {...props} />}
      tooltipText='Last reminder sent less than 24 hours ago'
    />
  );
}

function PrimaryButton(props: PropTypes) {
  if (!isEditable(props)) return;

  return (
    <IconButton
      iconName='bi-pencil'
      color='primary'
      buttonText='Edit'
      onClick={props.onEditOfferRequisition}
    />
  );
}

function setAlert(
  succeeded: boolean,
  setAlertMessage: (message: AlertObject) => void,
) {
  if (succeeded === true) {
    setAlertMessage({
      message: 'The offer requisition email reminder was successfully sent!',
      type: AlertType.Success,
    });
  } else if (succeeded === false) {
    setAlertMessage({
      message: 'Failed to send the offer requisition email reminder',
      type: AlertType.Danger,
    });
  }
}

export function OwnerOrAdminActions(props: PropTypes) {
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);

  return (
    <div className='d-flex align-items-center'>
      <SendReminder
        {...props}
        onSendReminderClick={() => setIsReminderModalOpen(true)}
      />
      <PrimaryButton {...props} />
      {isReminderModalOpen && (
        <SendReminderModal
          {...props}
          offerRequisitionId={props.offerRequisition.id}
          onClose={(succeeded: boolean) => {
            setAlert(succeeded, props.setAlertMessage);
            succeeded && props.reloadOfferRequisition();
            setIsReminderModalOpen(false);
          }}
        />
      )}
    </div>
  );
}
